import * as Scrivito from 'scrivito';
import React from 'react';
import { AccordionWidgetAttributes } from './accordion-widget-definitions';
import { AccordionWidget, AccordionWidgetClass } from './accordion-widget-class';
import { AccordionProps } from '../../components/controls/accordion/accordion';
import { AccordionElementWidgetAttributes } from '../accordion-element-widget/accordion-element-widget-definitions';
import { spacingClassName } from '../../utils/scrivito/spacing-definitions';
import { contentTagOrHtml } from '../../utils/scrivito/widget.utils';
import { AccordionList } from '../../components/controls/accordion/accordion-list';
import { ResponsiveImage } from '../../components/controls/image/responsive-image/responsive-image.scrivito';
import { ImageObjectAttributes } from '../../objects';
import styles from './accordion-widget.module.scss';
import classNames from 'classnames';

const renderImageReference = (element: Scrivito.Widget, attribute: string): React.ReactNode => {
  const image = element.get(attribute) as Scrivito.Obj | null;
  const imageBlob = image?.get('blob') as Scrivito.Binary | undefined;

  if (image && imageBlob) {
    const alt = image.get(ImageObjectAttributes.ALTERNATIVE_TEXT) as string;
    const title = image.get(ImageObjectAttributes.TITLE) as string;
    return <ResponsiveImage alt={alt} binary={imageBlob} title={title} />;
  }

  if (Scrivito.isInPlaceEditingActive()) {
    return <Scrivito.ImageTag content={element} attribute={attribute} />;
  }

  return null;
};

export const AccordionWidgetComponent: React.FC<{ widget: AccordionWidget }> = ({ widget }) => {
  const elements: Scrivito.Widget[] = widget.get(AccordionWidgetAttributes.ELEMENTS);
  const accordions: AccordionProps[] = elements?.map((element, index) => {
    const headlineText = element.get(AccordionElementWidgetAttributes.HEADLINE) as string;
    const headline = contentTagOrHtml(element, AccordionElementWidgetAttributes.HEADLINE, 'span');
    const subHeadline = contentTagOrHtml(element, AccordionElementWidgetAttributes.SUB_HEADLINE, 'span');
    const text = contentTagOrHtml(element, AccordionElementWidgetAttributes.TEXT, 'span');
    const id = (element.get(AccordionElementWidgetAttributes.ID) as string) || undefined;
    const buttons = element.get(AccordionElementWidgetAttributes.BUTTONS) as Scrivito.Widget[] | undefined;
    const showButtons = Boolean(buttons?.length) || Scrivito.isInPlaceEditingActive();

    const details = (
      <div className={styles.AccordionElementDetails}>
        {renderImageReference(element, AccordionElementWidgetAttributes.IMAGE_BEFORE)}
        {text}
        {renderImageReference(element, AccordionElementWidgetAttributes.IMAGE_AFTER)}
        {showButtons && (
          <Scrivito.ContentTag
            className={styles.AccordionElementButtons}
            content={element}
            attribute={AccordionElementWidgetAttributes.BUTTONS}
          />
        )}
      </div>
    );

    return {
      id: id,
      index: index,
      headline: headline,
      headlineText: headlineText,
      subHeadline: subHeadline,
      details: details,
    };
  });

  return (
    <Scrivito.WidgetTag>
      <div className={classNames(styles.AccordionWidget, spacingClassName(widget))}>
        {Boolean(accordions.length) && <AccordionList items={accordions} />}
      </div>
    </Scrivito.WidgetTag>
  );
};

Scrivito.provideComponent(AccordionWidgetClass, AccordionWidgetComponent);
